import { useNavigate, useLocation } from "react-router-dom";

interface UseBackNavigationProps {
  onBack?: () => void;
  backUrl?: string;
  // Optional state to re-attach when navigating to backUrl — a plain
  // navigate(path) (unlike navigate(-1)) starts a fresh history entry with
  // no state, which silently empties any destination page that renders
  // purely from location.state (e.g. a list passed in via navigation state
  // with no API refetch). Pass the original state back through here so it
  // survives the round trip.
  backState?: unknown;
}

export const useBackNavigation = ({
  onBack,
  backUrl,
  backState,
}: UseBackNavigationProps = {}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Try props, then location.state, then query param
  let resolvedBackUrl = backUrl;
  if (!resolvedBackUrl && location.state && (location.state as any).backUrl) {
    resolvedBackUrl = (location.state as any).backUrl;
  }
  if (!resolvedBackUrl) {
    const params = new URLSearchParams(location.search);
    const urlParam = params.get("backUrl");
    if (urlParam) resolvedBackUrl = urlParam;
  }

  let resolvedBackState = backState;
  if (
    resolvedBackState === undefined &&
    location.state &&
    (location.state as any).backState !== undefined
  ) {
    resolvedBackState = (location.state as any).backState;
  }

  const handleBack = () => {
    if (onBack) return onBack();
    if (resolvedBackUrl) {
      return navigate(
        resolvedBackUrl,
        resolvedBackState !== undefined
          ? { state: resolvedBackState }
          : undefined
      );
    }
    // Fallback: parent path
    const currentPath = window.location.pathname.replace(/\/$/, "");
    const pathSegments = currentPath.split("/").filter(Boolean);
    pathSegments.pop(); // Remove the last segment

    // Rebuild the path and navigate
    const newPath = `/${pathSegments.join("/")}`;
    return navigate(newPath);
  };

  return {
    handleBack,
  };
};
